import { Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { Column } from '../../decorators';
import { BaseEntity } from './base.entity';
import { ItemEntity } from './item.entity';
import { ShippingMethodEntity } from './shipping-method.entity';

/**
 * PD-15795 — items whose line total does NOT count toward this shipping
 * method's free-shipping threshold.
 *
 * The motivating case is a fee-like item on the order (Pharmsource sells a
 * "Rapid C2 Shipping Label"): it is a real, charged line, but letting its
 * cost push the order over the free-shipping threshold means the customer
 * earns free freight by buying a shipping surcharge. Excluding it here keeps
 * the line billable while leaving it out of the threshold comparison.
 *
 * This is deliberately NOT modelled on `shipping_method_restriction`, where
 * the presence of a row means "this value IS allowed". An exclusion says
 * nothing about whether the method may be used, only about how its threshold
 * is measured, so it gets its own table rather than a fourth restrictionType.
 *
 * It is also per-METHOD rather than a flag on the item: a tenant may want the
 * label excluded from an overnight method's $500 threshold while a ground
 * method has no threshold at all.
 */
@Index(
	'shipping_method_threshold_excluded_item_unique',
	['fkShippingMethodId', 'fkItemId'],
	{ unique: true },
)
@Index('shipping_method_threshold_excluded_item_item_idx', ['fkItemId'], {})
@Entity('shipping_method_threshold_excluded_item')
export class ShippingMethodThresholdExcludedItemEntity extends BaseEntity {
	@Column('int', {
		name: 'fk_shipping_method__id',
		unsigned: true,
	})
	public fkShippingMethodId!: number;

	@Column('int', {
		name: 'fk_item__id',
		unsigned: true,
	})
	public fkItemId!: number;

	@ManyToOne(
		() => ShippingMethodEntity,
		(shippingMethod) => shippingMethod.thresholdExcludedItems,
		{
			onDelete: 'CASCADE',
			onUpdate: 'CASCADE',
		},
	)
	@JoinColumn([{ name: 'fk_shipping_method__id', referencedColumnName: 'id' }])
	public shippingMethod!: ShippingMethodEntity;

	/**
	 * No inverse side on ItemEntity — nothing needs to read "which shipping
	 * methods exclude this item" from an item, and the item entity is already
	 * carrying twenty-odd collections. Mirrors ClientItemRecommendationEntity.
	 *
	 * Optional because only the admin read paths join the item through, to show
	 * its description and item number in the editor.
	 */
	@ManyToOne(() => ItemEntity, {
		onDelete: 'CASCADE',
		onUpdate: 'CASCADE',
	})
	@JoinColumn([{ name: 'fk_item__id', referencedColumnName: 'id' }])
	public item?: ItemEntity;
}
